import React from 'react';
import type { ComponentProps } from 'react';
import styled from '@emotion/styled';
import { ArticleLabelType } from 'graphql-ts-types';
import {
  alignItems,
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  display,
  flexDirection,
  minHeight,
  minWidth,
  position,
  right,
} from 'styled-system';
import type {
  AlignItemsProps,
  BorderColorProps,
  BorderRadiusProps,
  BorderStyleProps,
  BorderWidthProps,
  DisplayProps,
  FlexDirectionProps,
  MinHeightProps,
  MinWidthProps,
  PositionProps,
  RightProps,
} from 'styled-system';

import { useLocalization } from '../../providers/LocaleProvider';
import { flexGap } from '../../theme/system-utilities';
import type { FlexGapProps } from '../../theme/system-utilities';
import type { ArticleLabel } from '../../types/labels';
import Icon from '../Icon';
import { BodyBoldBB1, BodyRegularBR1, BodyRegularBR2 } from '../Typography';
import type { DisclaimerContent } from './utils';

const BUTLERS = 'BUTLERS';

interface LabelDrawerContentProps {
  type: ArticleLabel['type'];
  brand?: string;
  disclaimerContent: DisclaimerContent;
}

const SubContentContainer = styled(BodyRegularBR1)<
  DisplayProps &
    FlexDirectionProps &
    FlexGapProps &
    AlignItemsProps &
    BorderWidthProps &
    BorderStyleProps &
    BorderRadiusProps &
    BorderColorProps &
    PositionProps
>`
  ${display};
  ${flexDirection};
  ${alignItems}
  ${borderWidth};
  ${borderStyle};
  ${borderColor}
  ${flexGap};
  ${position};
  ${borderRadius};
`;

const subContentContainerStyles: ComponentProps<typeof SubContentContainer> = {
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  borderWidth: 'medium',
  borderStyle: 'solid',
  borderColor: 'sys.marketing.home24.tipp',
  borderRadius: 'medium',
  gap: 'sp_12',
};

const SubContent = styled(BodyRegularBR1)<
  DisplayProps &
    AlignItemsProps &
    MinHeightProps &
    MinWidthProps &
    FlexGapProps &
    PositionProps &
    RightProps
>`
  ${display};
  ${alignItems};
  ${minHeight};
  ${flexGap}
  ${minWidth}
  ${position};
  ${right};
`;

const subContentStyles: ComponentProps<typeof SubContent> = {
  display: 'flex',
  position: 'relative',
  alignItems: 'center',
  minHeight: '24px',
  minWidth: '200px',
  gap: 'sp_4',
};

const SUB_CONTENT_RIGHT_GAP = ['4px', null, null, '6px'];

export const parseExtraDisclaimerContentToText = (extraDisclaimerContent: string) => {
  const [{ text }] = JSON.parse(extraDisclaimerContent);

  return text;
};

const LabelDrawerContent = ({ brand, disclaimerContent, type }: LabelDrawerContentProps) => {
  const { t } = useLocalization();

  const mapContentBasedOnType = (
    labelType: ArticleLabel['type'],
    isButler: boolean,
    additionalContent: DisclaimerContent
  ) => {
    switch (labelType) {
      case ArticleLabelType.Campaign:
        return (
          <>
            {additionalContent.extraDisclaimerContent && (
              <BodyRegularBR2 p="sp_16" pb="sp_0">
                {parseExtraDisclaimerContentToText(additionalContent.extraDisclaimerContent)}
              </BodyRegularBR2>
            )}
            {additionalContent.legalDisclaimerContent && (
              <BodyRegularBR2 p="sp_16">{additionalContent.legalDisclaimerContent}</BodyRegularBR2>
            )}
          </>
        );
      case ArticleLabelType.Home24Selection:
        return (
          <>
            <BodyBoldBB1 color="sys.neutral.text.strong" p="sp_16">
              {t('h24_tipp_drawer-sub_heading')}
            </BodyBoldBB1>
            <BodyRegularBR1 color="sys.neutral.text.strong" p="sp_16" pt="sp_0">
              {t('h24_tipp_drawer_content')}
              <SubContentContainer {...subContentContainerStyles} p="sp_20" mt="sp_16">
                <SubContent {...subContentStyles} gap="sp_0" right={SUB_CONTENT_RIGHT_GAP}>
                  <Icon size={[26, null, null, 30]} name="truck" /> {t('h24_fast_delivery')}
                </SubContent>
                <SubContent {...subContentStyles}>
                  <Icon size={[16, null, null, 20]} name="starEmpty" />{' '}
                  {t('h24_customer_satisfaction')}
                </SubContent>
                <SubContent {...subContentStyles}>
                  <Icon size={[16, null, null, 20]} name="trophy" /> {t('h24_highest_quality')}
                </SubContent>
              </SubContentContainer>
            </BodyRegularBR1>
          </>
        );
      default:
        return (
          <BodyRegularBR2 p="sp_16">
            {isButler ? t('h24_label_legal_text_butler') : t('h24_label_legal_text_other')}
          </BodyRegularBR2>
        );
    }
  };

  const isButler = Boolean(brand?.includes(BUTLERS));

  return <div>{mapContentBasedOnType(type, isButler, disclaimerContent)}</div>;
};

export default LabelDrawerContent;
